import { on } from '@ember/modifier';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import {
  getSingleValueForTasField,
  type FieldName,
} from '../../../../utils/tuition-assistance/fields.ts';
import {
  ANALYSIS_TIMEOUT_MS,
  analysisErrorTags,
  analysisTimeoutTags,
  evaluateAiValidation,
  extractAiResult,
  type ServiceResponsePayload,
} from '../../../../utils/tuition-assistance/ai-validation.ts';
import {
  assetMatchesDocumentEntry,
  isLiveForClassification,
} from '../../../../utils/tuition-assistance/categorized-upload.ts';
import { PhPlus, PhWarningCircle } from 'ember-phosphor-icons';
import Component from '@glimmer/component';
import { findRecord } from '@warp-drive/utilities/json-api';
import { deleteExistingRecord, saveRecord } from 'tio-data/requests/save-record';
import DiscardedList from './discarded-list.gts';
import DocumentRow from './document-row.gts';
import type { DocumentAssetState, DocumentRowState, DocumentRowStatus } from './document-row.gts';
import type { DocumentType } from '../../../../types/tuition-assistance.ts';
import type CableService from '../../../../services/cable.ts';
import type DigitalAssetsService from '../../../../services/digital-assets.ts';
import type IntlService from 'ember-intl/services/intl';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

/** The three polymorphic models that can own TasAssets via a hasMany `tasAssets`. */
type Attachable = TasApplicationModel | TasCourseModel | TasProgramInstanceModel;

/** The attachable model names a caller passes as `@attachableType`. */
type AttachableType = 'tasApplication' | 'tasCourse' | 'tasProgramInstance';

/** TasAsset's inverse hasMany relationship keys. */
type AttachableRelationshipKey = 'tasProgramInstances' | 'tasCourses' | 'tasApplications';

/** Maps each attachable model name to TasAsset's matching hasMany relationship. */
const ATTACHABLE_RELATIONSHIP_KEY: Record<AttachableType, AttachableRelationshipKey> = {
  tasApplication: 'tasApplications',
  tasCourse: 'tasCourses',
  tasProgramInstance: 'tasProgramInstances',
};

export interface CategorizedUploadSignature {
  Args: {
    attachable: Attachable;
    attachableType: AttachableType;
    programTemplate: TasProgramTemplateModel;
    /** Approval-step classification (e.g. "COMPLETION_DOCUMENTS"). Drives both
     * the asset's `classification` AND the lookup of required document types. */
    classification: string;
    /** Admin-configured instruction text. When omitted, falls back to the
     * `${classification}_STRING` field on the program template. */
    instructions?: string;
    existingAssets?: TasAssetModel[];
    disabled?: boolean;
    onUploadComplete?: () => void;
  };
  Element: HTMLDivElement;
}

type RecoveredState = Pick<DocumentRowState, 'status' | 'feedback' | 'flagKind'>;

interface RecoveredTagSave {
  asset: TasAssetModel;
  tags: Record<string, unknown>;
}

/**
 * Categorized upload component. Replaces the legacy free-form upload UI for any
 * approval step whose `*_ATTACHMENT_FIELD` has a non-empty `document_types`
 * array on the program template. Steps without configured types should keep
 * using `UploadAttachments`.

 */
export default class CategorizedUpload extends Component<CategorizedUploadSignature> {
  @service declare cable: CableService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare intl: IntlService;
  @service declare store: StoreService;

  /**
   * Per-row state keyed by document type. Tracked as a plain object so a single
   * reassignment triggers re-render — Map mutations would not be reactive in
   * this glimmer version.
   */
  @tracked documentStates: Record<string, DocumentRowState> = {};

  /**
   * Soft-removed assets, kept in-memory for the lifetime of this component
   * instance to render the "Discarded:" tray (Figma B5+). Discarding here
   * destroys the attachment join on the server so the file no longer counts
   * toward the application; this list is purely UX history.
   */
  @tracked discardedAssets: TasAssetModel[] = [];

  /**
   * Extra rows added via "Add Item +". Always seeded as `general` so the
   * employee can attach supplemental documents that don't match a required
   * type. Each entry is an arbitrary key so multiples are allowed.
   */
  @tracked extraRowKeys: string[] = [];

  /**
   * Per-asset (uuid-keyed) timeout handles. Keyed by asset — not row — so a
   * second upload into the same row cannot orphan the first upload's timer.
   */
  private analysisTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Assets whose AI analysis was started by THIS component instance — the only
   * ones with a live cable subscription + timeout, and therefore the only ones
   * honestly "analyzing". A persisted `pending` tag restored from the store is
   * an orphaned verdict (tio-api#7874) and renders as not-validated instead so
   * the employee gets the use-anyways/discard escape (#9760). `willDestroy`
   * tears the set down together with its timers and subscriptions, so a
   * remount starts honest again.
   */
  #inFlightAssetUuids = new Set<string>();

  private pendingRecoveredTagSaves = new Map<string, RecoveredTagSave>();

  /**
   * The wizard unmounts this step as soon as the user advances; without
   * teardown the orphaned timers would fire against a destroyed component
   * (and could stamp timeout tags over a verdict persisted after remount).
   */
  willDestroy() {
    super.willDestroy();
    for (const timeoutId of this.analysisTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.analysisTimeouts.clear();
    for (const uuid of this.#inFlightAssetUuids) {
      this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: uuid });
    }
    this.#inFlightAssetUuids.clear();
  }

  private statusPriority(status: DocumentRowStatus) {
    switch (status) {
      case 'uploading':
      case 'analyzing':
        return 6;
      case 'flagged':
        return 5;
      case 'not_validated':
        return 4;
      case 'error':
        return 3;
      case 'accepted':
        return 2;
      case 'valid':
        return 1;
      default:
        return 0;
    }
  }

  private stateFromAssets(key: string, assets: TasAssetModel[]): DocumentRowState {
    if (assets.length === 0) {
      return { status: 'empty', asset: null, assets: [], assetStates: [], feedback: null };
    }

    const assetStates: DocumentAssetState[] = assets.map((asset) => ({
      asset,
      ...this.deriveStateFromAsset(asset, key),
    }));

    // Determine the overall row status. Transient states (uploading/analyzing)
    // always win so the user sees the spinner. Otherwise the *best* settled
    // status wins: a single valid doc means the category is satisfied even when
    // other uploads in the same row are flagged. The flagged banners still
    // render per-asset via `assetStates`.
    const transient = assetStates.find((s) => s.status === 'uploading' || s.status === 'analyzing');
    const valid = assetStates.find((s) => s.status === 'valid');
    const accepted = assetStates.find((s) => s.status === 'accepted');
    const primary =
      transient ??
      valid ??
      accepted ??
      assetStates.reduce((worst, current) =>
        this.statusPriority(current.status) > this.statusPriority(worst.status) ? current : worst
      );

    return {
      status: primary.status,
      asset: primary.asset,
      assets,
      assetStates,
      feedback: primary.feedback,
      flagKind: primary.flagKind,
    };
  }

  get instructionsField(): FieldName {
    return `${this.args.classification}_STRING` as FieldName;
  }

  /**
   * Admin-configured instruction text for this approval step. Prefer an explicit
   * `@instructions` (the guided wizard joins per-stage strings for combined
   * program types); otherwise read `${classification}_STRING` off the template —
   * mirrors the legacy `UploadAttachments` lookup so categorized steps don't drop
   * the description (see tuitionio/tio-front-end#8856).
   */
  get instructions(): string {
    return (
      this.args.instructions ??
      getSingleValueForTasField(this.instructionsField, this.args.programTemplate?.fields, '')
    );
  }

  get requiredDocTypes(): DocumentType[] {
    return this.args.programTemplate.requiredDocumentTypesFor(this.args.classification);
  }

  /**
   * All admin-configured entries (label + type) for the current classification,
   * including `general` items. Used to render one row per attachment the admin
   * configured, with their custom label.
   */
  get documentEntries(): Array<{ label: string; type: DocumentType }> {
    return this.args.programTemplate.documentEntriesFor(this.args.classification);
  }

  /**
   * Map from label → type for fast lookup when the label is used as a row key.
   */
  private get labelToType(): Map<string, DocumentType> {
    const m = new Map<string, DocumentType>();
    for (const entry of this.documentEntries) {
      m.set(entry.label, entry.type);
    }
    return m;
  }

  /** The employee's full name for identity verification against AI-detected names. */
  get employeeName(): string {
    const attachable = this.args.attachable;
    // TasApplication → tasProgramInstance → employee → person
    const programInstance =
      'tasProgramInstance' in attachable
        ? (attachable as TasApplicationModel).tasProgramInstance
        : null;
    const person = programInstance?.employee?.person;
    return person?.fullName ?? '';
  }

  private get expectedInstitutionName() {
    const attachable = this.args.attachable;
    if ('institutionName' in attachable) return attachable.institutionName;
    if ('tasProgramInstance' in attachable)
      return attachable.tasProgramInstance?.institutionName ?? '';
    if ('tasApplication' in attachable) {
      return attachable.tasApplication?.tasProgramInstance?.institutionName ?? '';
    }
    return '';
  }

  private get expectedCourseNames() {
    const attachable = this.args.attachable;
    if ('courseName' in attachable) return [attachable.courseName].filter(Boolean);
    if ('activeCourses' in attachable) {
      return attachable.activeCourses.map((course) => course.courseName).filter(Boolean);
    }
    if ('tasApplications' in attachable) {
      return attachable.tasApplications
        .flatMap((application) => application.activeCourses.map((course) => course.courseName))
        .filter(Boolean);
    }
    return [];
  }

  /**
   * Returns the row state, falling back to `empty` when nothing has been
   * uploaded for that type yet. Existing assets attached to the application
   * before this component mounted are surfaced here.
   *
   * When the key is an admin-configured label, match existing assets by their
   * employeeSelectedType (the type derived from the label→type map). Multiple
   * files may belong to the same bucket, so keep every matching live asset.
   */
  stateFor = (key: string): DocumentRowState => {
    const tracked = this.documentStates[key];
    if (tracked) return tracked;

    // For label-keyed rows, match by the underlying document type. Membership
    // (row-key first, declared-type fallback) is shared with the wizard's
    // submission gate so the two can never disagree (#9760).
    const mappedType = this.labelToType.get(key);
    const matchType = (mappedType ?? key) as DocumentType;

    const existingAssets = (this.args.existingAssets ?? []).filter(
      (a) =>
        isLiveForClassification(a, this.args.classification) &&
        assetMatchesDocumentEntry(a, { label: key, type: matchType })
    );
    return this.stateFromAssets(key, existingAssets);
  };

  get uploadedCount(): number {
    return this.documentEntries.filter((entry) => {
      const state = this.stateFor(entry.label);
      return state.status !== 'empty';
    }).length;
  }

  get flaggedCount(): number {
    return this.documentEntries.reduce(
      (count, entry) =>
        count +
        this.stateFor(entry.label).assetStates.filter((assetState) =>
          ['flagged', 'not_validated', 'error'].includes(assetState.status)
        ).length,
      0
    );
  }

  /** Map persisted tag state back to a row state when re-rendering. */
  private deriveStateFromAsset(asset: TasAssetModel, key: string): RecoveredState {
    const aiResult = extractAiResult(asset.assetSummary?.summary);
    if (aiResult && asset.aiValidationStatus === 'pending') {
      const outcome = this.validationOutcomeForAiResult(key, aiResult);
      this.scheduleRecoveredTagSave(asset, key, outcome.tags);
      return {
        status: outcome.status,
        feedback: outcome.feedback,
        flagKind: outcome.flagKind,
      };
    }

    const displayStatus = asset.aiValidationDisplayStatus;
    const tagStatus = asset.aiValidationStatus;
    if (tagStatus === 'valid') return { status: 'valid', feedback: null };
    if (tagStatus === 'accepted') return { status: 'accepted', feedback: null };
    if (displayStatus === 'not_validated') return { status: 'not_validated', feedback: null };
    if (tagStatus === 'flagged' && aiResult) {
      const outcome = this.validationOutcomeForAiResult(key, aiResult);
      return {
        status: outcome.status,
        feedback: outcome.feedback,
        flagKind: outcome.flagKind,
      };
    }
    if (tagStatus === 'flagged')
      return { status: 'flagged', feedback: asset.aiValidationFeedback ?? null };
    if (tagStatus === 'error') return { status: 'error', feedback: asset.aiValidationFeedback };
    if (tagStatus === 'pending') {
      // Only an upload from this session is genuinely mid-analysis (live
      // subscription + timeout). A `pending` restored from persistence is an
      // orphaned verdict — the broadcast was missed and nothing will ever
      // settle it client-side (tio-api#7874) — so offer use-anyways/discard
      // rather than a dead-end spinner (#9760).
      if (this.#inFlightAssetUuids.has(asset.uuid)) {
        return { status: 'analyzing', feedback: null };
      }
      return { status: 'not_validated', feedback: null };
    }
    // No AI tag yet (legacy upload): treat as a previously-uploaded file.
    return { status: 'valid', feedback: null };
  }

  private scheduleRecoveredTagSave(
    asset: TasAssetModel,
    key: string,
    tags: Record<string, unknown>
  ) {
    this.pendingRecoveredTagSaves.set(`${asset.id ?? asset.uuid}:${key}`, { asset, tags });

    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.persistRecoveredTags);
  }

  private persistRecoveredTags = () => {
    const pending = [...this.pendingRecoveredTagSaves.values()];
    this.pendingRecoveredTagSaves.clear();

    for (const { asset, tags } of pending) {
      if (asset.aiValidationStatus !== 'pending') continue;

      asset.tags = tags;
      void saveRecord(this.store, asset).catch((e: unknown) => {
        console.warn('Failed to persist recovered AI validation tags', e);
      });
    }
  };

  private recoverAnalysisFromAsset = async (docType: string, asset: TasAssetModel) => {
    try {
      await this.store.request(
        findRecord<TasAssetModel>('tas-asset', asset.id!, {
          include: 'asset-summary',
          reload: true,
        })
      );
    } catch (e) {
      console.warn('Failed to reload asset for recovered AI validation', e);
      return false;
    }
    if (this.isDestroying || this.isDestroyed) return false;

    const aiResult = extractAiResult(asset.assetSummary?.summary);
    if (!aiResult) return false;

    const outcome = this.validationOutcomeForAiResult(docType, aiResult);
    asset.tags = outcome.tags;
    try {
      await saveRecord(this.store, asset);
    } catch (e) {
      console.warn('Failed to persist recovered AI validation tags', e);
    }
    if (this.isDestroying || this.isDestroyed) return true;

    this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
    this.args.onUploadComplete?.();
    return true;
  };

  private clearAnalysisTimeout(assetUuid: string) {
    const existingTimeout = this.analysisTimeouts.get(assetUuid);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.analysisTimeouts.delete(assetUuid);
    }
  }

  /**
   * Evaluate an AI result via the shared evaluator (utils/tuition-assistance/
   * ai-validation.ts) — the admin approval drawers run the exact same logic.
   */
  private validationOutcomeForAiResult(docType: string, aiResult: Record<string, unknown>) {
    return evaluateAiValidation(aiResult, {
      declaredType: this.actualDocType(docType),
      uploadRowKey: docType,
      employeeName: this.employeeName,
      expectedInstitutionName: this.expectedInstitutionName,
      expectedCourseNames: this.expectedCourseNames,
      translate: (key: string, options?: Record<string, string>) => this.intl.t(key, options),
    });
  }

  /**
   * Persist a single row's state by replacing the entire object. Required
   * because a tracked `Record` only re-renders consumers when reassigned.
   */
  private updateDocState(docType: string, partial: Partial<DocumentRowState>) {
    this.documentStates = {
      ...this.documentStates,
      [docType]: {
        ...this.stateFor(docType),
        ...partial,
      },
    };
  }

  private appendAssetToDocState(
    docType: string,
    asset: TasAssetModel,
    partial: Partial<DocumentRowState>
  ) {
    const current = this.stateFor(docType);
    const assets = current.assets.some((existing) => existing === asset || existing.id === asset.id)
      ? current.assets
      : [...current.assets, asset];

    this.documentStates = {
      ...this.documentStates,
      [docType]: {
        ...this.stateFromAssets(docType, assets),
        asset,
        ...partial,
      },
    };
  }

  private refreshDocStateFromAssets(docType: string, assets: TasAssetModel[]) {
    this.documentStates = {
      ...this.documentStates,
      [docType]: this.stateFromAssets(docType, assets),
    };
  }

  /**
   * Extra row keys start with "extra-"; for those the actual document type
   * persisted on the asset is always "general". For admin-configured label keys,
   * look up the corresponding type from the documentEntries map.
   */
  private actualDocType = (key: string): DocumentType => {
    if (key.startsWith('extra-')) return 'general';
    const mapped = this.labelToType.get(key);
    if (mapped) return mapped;
    // Fallback: treat the key as a literal DocumentType (legacy path).
    return key as DocumentType;
  };

  handleUpload = async (docType: string, file: unknown) => {
    this.updateDocState(docType, { status: 'uploading', feedback: null });

    // 1. Create the TasAsset record from the S3 upload details and tag it with
    //    the employee's declared type plus a pending validation marker.
    const asset = this.digitalAssets.createAssetFromS3Details(
      'tasAsset',
      file as Parameters<DigitalAssetsService['createAssetFromS3Details']>[1]
    ) as TasAssetModel;
    asset.classification = this.args.classification;
    const actualType = this.actualDocType(docType);
    asset.tags = {
      uploadRowKey: docType,
      employeeSelectedType: actualType,
      aiValidationStatus: actualType === 'general' ? 'valid' : 'pending',
      aiValidationDisplayStatus: actualType === 'general' ? 'valid' : 'pending',
      skipAiAnalysis: String(actualType === 'general'),
    };

    // 2. Link to the attachable side of the relationship. Saves any new parent
    //    records first so the polymorphic relationship has IDs to point at.
    const attachable: Attachable = this.args.attachable;
    const relationshipKey = ATTACHABLE_RELATIONSHIP_KEY[this.args.attachableType];

    if (attachable.isNew) {
      // Only TasCourse has a `tasApplication` belongsTo; only TasApplication has
      // `tasProgramInstance`. Narrow the union before access.
      if ('tasApplication' in attachable && attachable.tasApplication?.isNew) {
        await saveRecord(this.store, attachable.tasApplication);
      }
      if ('tasProgramInstance' in attachable && attachable.tasProgramInstance?.isNew) {
        await saveRecord(this.store, attachable.tasProgramInstance);
      }
      await saveRecord(this.store, attachable);
    }
    // hasMany ManyArrays whose element types differ per variant; cast to the
    // loosest common element shape for the structurally-identical push.
    (asset[relationshipKey] as Attachable[]).push(attachable);
    attachable.tasAssets.push(asset);

    await saveRecord(this.store, asset);
    await saveRecord(this.store, attachable);

    if (actualType === 'general') {
      this.appendAssetToDocState(docType, asset, { status: 'valid', feedback: null });
      this.args.onUploadComplete?.();
      return;
    }

    // 3. Subscribe to the ActionCable channel using the asset UUID. The AI
    //    agent broadcasts on this same `request_id` once Gemini finishes.
    this.#inFlightAssetUuids.add(asset.uuid);
    const channelParams = {
      channel: 'ServiceResponseChannel',
      request_id: asset.uuid,
    };
    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        this.handleAiResponse(docType, asset, data as ServiceResponsePayload);
      },
    });

    this.appendAssetToDocState(docType, asset, { status: 'analyzing', feedback: null });

    // 4. Fall back to "not validated" if the AI takes too long.
    //    The employee can still use the document via "Use Anyways".
    //    Timers are per-asset so parallel uploads into one row each keep theirs.
    const timeoutId = setTimeout(() => {
      void (async () => {
        this.analysisTimeouts.delete(asset.uuid);
        // Already settled by the broadcast, removed, or torn down — nothing to do.
        if (!this.#inFlightAssetUuids.has(asset.uuid)) return;

        this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
        this.#inFlightAssetUuids.delete(asset.uuid);

        const recovered = await this.recoverAnalysisFromAsset(docType, asset);
        if (this.isDestroying || this.isDestroyed) return;
        // Re-check the asset itself: a broadcast (or a remount's use-anyways)
        // may have landed a verdict while the recovery reload was in flight —
        // never stamp timeout tags over a settled verdict.
        if (!recovered && asset.aiValidationStatus === 'pending') {
          asset.tags = analysisTimeoutTags(
            asset.tags,
            asset.employeeSelectedType ?? this.actualDocType(docType)
          );
          void saveRecord(this.store, asset).catch((e: unknown) => {
            console.warn('Failed to persist timeout validation status', e);
          });
          this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
        }
      })();
    }, ANALYSIS_TIMEOUT_MS);
    this.analysisTimeouts.set(asset.uuid, timeoutId);
  };

  handleAiResponse = async (
    docType: string,
    asset: TasAssetModel,
    data: ServiceResponsePayload
  ) => {
    this.clearAnalysisTimeout(asset.uuid);

    // Always unsubscribe — the AI agent only broadcasts once per asset.
    this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
    this.#inFlightAssetUuids.delete(asset.uuid);

    const summary = data?.data?.summary;
    if (!summary) {
      asset.tags = analysisErrorTags(
        asset.tags,
        asset.employeeSelectedType ?? this.actualDocType(docType),
        (key: string, options?: Record<string, string>) => this.intl.t(key, options)
      );
      try {
        await saveRecord(this.store, asset);
      } catch (e) {
        console.warn('Failed to persist AI validation error status', e);
      }
      this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
      return;
    }

    // The AI response wraps the Gemini result inside a JSONAPI resource:
    //   data.summary = { id, type, attributes: { summary: { result: { ...ai fields } } } }
    // Unwrap to get the raw AI analysis. Fall back to the top-level summary
    // in case the payload arrives pre-flattened.
    const aiResult = extractAiResult(summary);

    if (!aiResult) {
      asset.tags = analysisErrorTags(
        asset.tags,
        asset.employeeSelectedType ?? this.actualDocType(docType),
        (key: string, options?: Record<string, string>) => this.intl.t(key, options)
      );
      try {
        await saveRecord(this.store, asset);
      } catch (e) {
        console.warn('Failed to persist AI validation error status', e);
      }
      if (this.isDestroying || this.isDestroyed) return;
      this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);
      return;
    }

    const outcome = this.validationOutcomeForAiResult(docType, aiResult);

    asset.tags = outcome.tags;
    try {
      await saveRecord(this.store, asset);
    } catch (e) {
      // Persisting tags is best-effort; the UI still reflects the result. We
      // surface a console warning rather than blocking the employee.
      console.warn('Failed to persist AI validation tags', e);
    }
    if (this.isDestroying || this.isDestroyed) return;

    this.refreshDocStateFromAssets(docType, this.stateFor(docType).assets);

    this.args.onUploadComplete?.();
  };

  handleRemove = async (docType: string, assetToRemove?: TasAssetModel) => {
    const state = this.stateFor(docType);
    const asset = assetToRemove ?? state.asset;

    // Clean up the removed asset's in-flight subscription / timer.
    if (asset) {
      this.clearAnalysisTimeout(asset.uuid);
      this.cable.unsubscribe({ channel: 'ServiceResponseChannel', request_id: asset.uuid });
      this.#inFlightAssetUuids.delete(asset.uuid);
    }

    if (!asset) {
      this.updateDocState(docType, {
        status: 'empty',
        asset: null,
        assets: [],
        assetStates: [],
        feedback: null,
        flagKind: undefined,
      });
      return;
    }
    // Capture for the discarded tray BEFORE destroying the join — the asset
    // still has its filename loaded at this point.
    const discardedSnapshot = asset;
    // Mirror the legacy attachment-list removal: destroy the attachment join
    // and reload the asset so it shows as discarded for reviewers.
    // Freshly uploaded assets may not have attachments sideloaded in the store,
    // so reload first to ensure the relationship is populated.
    let attachment = asset.attachments[0];
    if (!attachment) {
      try {
        await this.store.request(
          findRecord<TasAssetModel>('tas-asset', asset.id!, { reload: true })
        );
        attachment = asset.attachments[0];
      } catch (e) {
        console.warn('Failed to reload asset for attachment lookup', e);
      }
    }
    if (attachment) {
      try {
        await deleteExistingRecord(this.store, attachment);
        await this.store.request(
          findRecord<TasAssetModel>('tas-asset', asset.id!, { reload: true })
        );
      } catch (e) {
        console.warn('Failed to remove attachment', e);
      }
    }
    this.discardedAssets = [...this.discardedAssets, discardedSnapshot];
    this.refreshDocStateFromAssets(
      docType,
      state.assets.filter((existing) => existing !== asset && existing.id !== asset.id)
    );
  };

  /**
   * "Use anyways" collapses the flagged/not-validated banner into the standard
   * uploaded state without removing the asset. The validation tags persist on
   * the asset so reviewers still see the warning later.
   */
  handleUseAnyways = async (docType: string, assetToAccept?: TasAssetModel) => {
    const state = this.stateFor(docType);
    const asset = assetToAccept ?? state.asset;
    if (asset) {
      asset.tags = {
        uploadRowKey: asset.uploadRowKey ?? docType,
        employeeSelectedType: asset.employeeSelectedType ?? this.actualDocType(docType),
        aiDetectedTypes: asset.aiDetectedTypes,
        aiValidationStatus: 'accepted',
        aiValidationDisplayStatus: 'accepted',
        aiValidationFeedback: asset.aiValidationFeedback,
        aiTypeMatch: String(asset.aiTypeMatch ?? ''),
        aiIdentityMatch: String(asset.aiIdentityMatch ?? ''),
      };
      try {
        await saveRecord(this.store, asset);
      } catch (e) {
        console.warn('Failed to persist accepted status', e);
      }
    }
    this.refreshDocStateFromAssets(docType, state.assets);
  };

  /**
   * Adds a new free-form upload slot. Always typed `general` so the AI agent
   * still classifies it but no required-type slot is reserved.
   */
  handleAddItem = () => {
    this.extraRowKeys = [...this.extraRowKeys, `extra-${this.extraRowKeys.length}-${Date.now()}`];
  };

  <template>
    <div class="tio-col" ...attributes>
      {{#if this.instructions}}
        <p
          class="tio-text-sm-medium tio-fg-primary whitespace-pre-line"
          data-test-categorized-upload-instructions
        >
          {{this.instructions}}
        </p>
      {{/if}}
      <p class="text-sm tio-fg-muted">
        {{t "tas.attachments.intro"}}
      </p>
      <p class="text-xs tio-fg-muted">
        {{t "tas.attachments.formats"}}
      </p>

      {{! Section header — "Required Documents" + Add Item link, per Figma. }}
      <div class="tio-row-between pb-1">
        <h4 class="tio-text-sm-semibold uppercase tracking-wider tio-fg-muted">
          {{t "tas.attachments.required_documents"}}
        </h4>
        <button
          type="button"
          class="tio-inline-row-extra-tight tio-text-sm-medium tio-fg-info hover:tio-fg-info-h"
          {{on "click" this.handleAddItem}}
          data-test-add-item
        >
          {{t "tas.attachments.add_item"}}
          <PhPlus @size="14" />
        </button>
      </div>

      <div class="tio-col-tight">
        {{#each this.documentEntries as |entry|}}
          <DocumentRow
            @docType={{entry.type}}
            @label={{entry.label}}
            @state={{this.stateFor entry.label}}
            @onUpload={{fn this.handleUpload entry.label}}
            @onRemove={{fn this.handleRemove entry.label}}
            @onUseAnyways={{fn this.handleUseAnyways entry.label}}
            @disabled={{@disabled}}
          />
        {{/each}}
        {{#each this.extraRowKeys as |rowKey|}}
          {{! Extra "Add Item" rows are always classified as general — the AI
            still validates them for completeness/identity but they do not
            count toward the required-types tally. Each row uses its unique key
            for state tracking so multiple extras don't share state. }}
          <DocumentRow
            @docType="general"
            @state={{this.stateFor rowKey}}
            @onUpload={{fn this.handleUpload rowKey}}
            @onRemove={{fn this.handleRemove rowKey}}
            @onUseAnyways={{fn this.handleUseAnyways rowKey}}
            @disabled={{@disabled}}
          />
        {{/each}}
      </div>

      <DiscardedList @assets={{this.discardedAssets}} />

      <div class="tio-row-between text-sm">
        <span class="tio-fg-muted" data-test-counter>
          {{this.uploadedCount}}
          /
          {{this.documentEntries.length}}
          {{t "tas.attachments.required"}}
        </span>
        {{#if this.flaggedCount}}
          <span
            class="tio-inline-row-extra-tight font-semibold tio-fg-danger-body"
            data-test-flagged-count
          >
            <PhWarningCircle @size="14" @weight="fill" />
            {{t "tas.attachments.flagged_count" count=this.flaggedCount}}
          </span>
        {{/if}}
      </div>
    </div>
  </template>
}
